import React, { useContext, useEffect, useState } from 'react';
import {
  Paper, Typography, Box, Button, Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, Autocomplete, Chip, Alert, CircularProgress
} from '@mui/material';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import { useCurrency } from '../../context/CurrencyContext';
import { AuthContext } from '../../context/AuthContext';
import ScrollTable from '../ScrollTable';

const TenantExitManagement = () => {
  const { formatMoney } = useCurrency();
  const { can } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({
    tenantId: '',
    terms: 'good',
    reason: 'voluntary',
    intendedVacateDate: '',
    notes: ''
  });

  const load = async () => {
    setLoading(true);
    try {
      const exitsRes = await axios.get('/tenant-exits');
      setRows(exitsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Could not load exits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // The tenants endpoint caps a page at 100, so the picker searches the server as the user
  // types rather than filtering a single fetched page.
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setTenantsLoading(true);
      try {
        const res = await axios.get('/tenants', { params: { limit: 100, search: tenantSearch.trim() } });
        const body = res.data;
        const list = Array.isArray(body) ? body : (body?.data || []);
        if (!cancelled) setTenants(list.filter((t) => t.unitId || t.unit));
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || 'Could not load tenants');
      } finally {
        if (!cancelled) setTenantsLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, tenantSearch]);

  const create = async () => {
    try {
      await axios.post('/tenant-exits', form);
      toast.success('Exit opened');
      setOpen(false);
      setSelectedTenant(null);
      setTenantSearch('');
      setForm({ tenantId: '', terms: 'good', reason: 'voluntary', intendedVacateDate: '', notes: '' });
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not open exit');
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`/tenant-exits/${id}`);
      setDetail(res.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not load exit');
    }
  };

  const release = async (allowOpenBalance = false) => {
    if (!detail?.id) return;
    try {
      await axios.post(`/tenant-exits/${detail.id}/release`, { allowOpenBalance });
      toast.success('Unit released; tenancy ended');
      setDetail(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not release');
      if (error.response?.data?.account) {
        setDetail((d) => ({ ...d, account: error.response.data.account }));
      }
    }
  };

  const cancel = async () => {
    if (!detail?.id) return;
    try {
      await axios.post(`/tenant-exits/${detail.id}/cancel`, { reason: 'Cancelled by staff' });
      toast.success('Exit cancelled');
      setDetail(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not cancel');
    }
  };

  const statusColor = (s) => (s === 'completed' ? 'success' : s === 'cancelled' ? 'default' : 'warning');

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Tenant exits</Typography>
        {can('tenantExits', 'create') && (
          <Button variant="contained" onClick={() => setOpen(true)}>Open exit</Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Independent of rent increases. Settle any held security deposit before releasing the unit.
      </Typography>

      {loading ? <CircularProgress /> : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell>Unit</TableCell>
              <TableCell>Terms</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Vacate</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>{r.tenant?.name || '—'}</TableCell>
                <TableCell>{r.unit?.unitNumber || '—'}</TableCell>
                <TableCell sx={{ textTransform: 'capitalize' }}>{r.terms}</TableCell>
                <TableCell>{r.reason}</TableCell>
                <TableCell>{r.intendedVacateDate || '—'}</TableCell>
                <TableCell><Chip size="small" label={r.status} color={statusColor(r.status)} /></TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openDetail(r.id)}>Open</Button>
                </TableCell>
              </TableRow>
            ))}
            {!rows.length && (
              <TableRow><TableCell colSpan={7}>No exits yet.</TableCell></TableRow>
            )}
          </TableBody>
        </ScrollTable>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Open tenant exit</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Autocomplete
            options={tenants}
            loading={tenantsLoading}
            // Filtering is done by the server; the default client filter would hide matches
            // on phone or email that are not in the label.
            filterOptions={(x) => x}
            value={selectedTenant}
            onChange={(e, t) => {
              setSelectedTenant(t);
              setForm({ ...form, tenantId: t ? (t.id || t._id) : '' });
            }}
            onInputChange={(e, v, reason) => { if (reason === 'input') setTenantSearch(v); }}
            getOptionLabel={(t) => `${t.name}${t.unit?.unitNumber ? ` — ${t.unit.unitNumber}` : ''}`}
            isOptionEqualToValue={(a, b) => (a.id || a._id) === (b.id || b._id)}
            renderOption={(props, t) => (
              <li {...props} key={t.id || t._id}>
                {t.name}{t.unit?.unitNumber ? ` — ${t.unit.unitNumber}` : ''}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label="Tenant" placeholder="Type to search" />}
          />
          <FormControl fullWidth>
            <InputLabel>Terms</InputLabel>
            <Select label="Terms" value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })}>
              <MenuItem value="good">Good terms</MenuItem>
              <MenuItem value="bad">Bad terms</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Reason"
            select
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          >
            <MenuItem value="voluntary">Voluntary</MenuItem>
            <MenuItem value="lease_end">Lease end</MenuItem>
            <MenuItem value="arrears">Arrears</MenuItem>
            <MenuItem value="breach">Breach</MenuItem>
            <MenuItem value="abandonment">Abandonment</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>
          <TextField
            label="Intended vacate date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.intendedVacateDate}
            onChange={(e) => setForm({ ...form, intendedVacateDate: e.target.value })}
          />
          <TextField
            label="Notes"
            multiline
            minRows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.tenantId} onClick={create}>Open</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle>
          Exit — {detail?.tenant?.name || ''}
          {detail?.status && <Chip size="small" sx={{ ml: 1 }} label={detail.status} color={statusColor(detail.status)} />}
        </DialogTitle>
        <DialogContent>
          {detail?.account && (
            <Box mb={2}>
              <Alert severity={detail.account.totals?.canRelease ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {detail.account.totals?.canRelease
                  ? 'No held security deposit — unit can be released.'
                  : 'Settle held security deposit (Deposits) before releasing the unit.'}
              </Alert>
              <Typography variant="subtitle2">Final account</Typography>
              <Typography variant="body2">
                Open invoices: {formatMoney(detail.account.totals?.invoiceBalance || 0)}
              </Typography>
              <Typography variant="body2">
                Deposit held: {formatMoney(detail.account.totals?.depositHeld || 0)}
              </Typography>
              <Typography variant="body2">
                Advances outstanding (net): {formatMoney(detail.account.totals?.advanceNet || 0)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {detail?.status === 'open' && (
            <>
              {can('tenantExits', 'update') && (
                <Button color="inherit" onClick={cancel}>Cancel exit</Button>
              )}
              {can('tenantExits', 'release') && (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => release(true)}
                    disabled={!detail?.account?.totals?.canRelease}
                  >
                    Release (allow open balance)
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => release(false)}
                    disabled={!detail?.account?.totals?.canRelease}
                  >
                    Release unit
                  </Button>
                </>
              )}
            </>
          )}
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TenantExitManagement;
